import { Button, Icons, InputField, Tooltip } from 'plume-ui';
import { FunctionComponent, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { useFeaturePermission } from '../../../../../../../hooks/useFeaturePermission';
import { outreachTestMessageModalAtom } from '../../../../../../../state';
import { OutreachTypes } from '../../../../../../../urls';
import { Permission } from '../../../../../../../userPermissionConfig';
import FormattedMessage from '../../../../../../../utils/components/FormattedMessage';
import OutreachConfigFieldMessages from '../../../../../../Outreach/components/OutreachConfigFieldMessages';
import {
  FormikMessageField,
  OutreachConfigGenericFormField,
  OutreachConfigSections,
  SupportOutreachClearMessageFormFields,
} from '../../../../../types';
import OutreachConfigSection from '../../../OutreachConfigSection';
import useFormControls from '../../../hooks/useFormControls';
import useValidation from '../../../hooks/useValidation';

export type FormikSupportOutreachClearMessageValue = {
  supportOutreachTime?: {
    from: Date;
    to: Date;
  };
} & FormikMessageField;

const FormikSupportOutreachClearMessageField: FunctionComponent<OutreachConfigGenericFormField<
  FormikSupportOutreachClearMessageValue
>> = ({ field, form }) => {
  const { t } = useTranslation();
  const { isSectionComplete } = useValidation();
  const { hasPermission } = useFeaturePermission();
  const { setPropertyValue } = useFormControls<
    FormikSupportOutreachClearMessageValue
  >(field, form);
  const setOutreachTestMessageModal = useSetRecoilState(
    outreachTestMessageModalAtom,
  );
  const canEditSupportEmail = hasPermission(
    Permission.EditSupportEmailOutreach,
  );
  const titleCopyId = 'outreach.form.supportOutreachClearMessageSection.title';
  const subtitleCopyId =
    'outreach.form.supportOutreachClearMessageSection.subtitle';

  const onClickTestNotification = () => {
    setOutreachTestMessageModal({
      isOpen: true,
      messageType: 'clear',
      outreachType: OutreachTypes.Email,
      ctaUrl: undefined,
    });
  };

  const renderMessageTemplate = (): string[] => {
    const keys: {
      [key: string]: string;
    } = t('outreach.form.supportOutreachContentSection.templateLines', {
      returnObjects: true,
    });
    return Object.keys(keys).map((key) => keys[key as keyof typeof keys]);
  };

  const isComplete = isSectionComplete(
    OutreachConfigSections.SupportOutreachClearMessage,
  );

  return (
    <OutreachConfigSection
      title={t(titleCopyId)}
      subtitle={t(subtitleCopyId)}
      isRequired
      isComplete={isComplete}
      isOpen={canEditSupportEmail}
    >
      <div className="OutreachContainer__section-content-wrapper">
        <div className="OutreachContainer__outreach-content-row OutreachContainer__outreach-content-row--horizontal-form">
          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <span className="color-required">* </span>
              <FormattedMessage id="outreach.form.supportOutreachClearMessageSection.subject" />
            </div>
            <div className="OutreachContainer__outreach-content-field-input">
              <InputField
                name={SupportOutreachClearMessageFormFields.Subject}
                type="text"
                value={
                  field.value[SupportOutreachClearMessageFormFields.Subject]
                }
                onInput={(e) =>
                  setPropertyValue(
                    SupportOutreachClearMessageFormFields.Subject,
                    e.currentTarget.value,
                  )
                }
              />
              <OutreachConfigFieldMessages
                propertyName={SupportOutreachClearMessageFormFields.Subject}
                field={field}
                form={form}
              />
            </div>
          </div>

          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <span className="color-required">* </span>
              <FormattedMessage id="outreach.form.supportOutreachClearMessageSection.introduction" />
            </div>
            <div className="OutreachContainer__outreach-content-field-input">
              <InputField
                onInput={(e) =>
                  setPropertyValue(
                    SupportOutreachClearMessageFormFields.Introduction,
                    e.currentTarget.value,
                  )
                }
                value={
                  field.value[
                    SupportOutreachClearMessageFormFields.Introduction
                  ]
                }
              />
              <OutreachConfigFieldMessages
                propertyName={
                  SupportOutreachClearMessageFormFields.Introduction
                }
                field={field}
                form={form}
              />
            </div>
          </div>
          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              <FormattedMessage id="outreach.form.supportOutreachClearMessageSection.messageTemplate" />
            </div>
            <div className="OutreachContainer__outreach-content-field-content">
              {renderMessageTemplate()
                .filter(Boolean)
                .map((line: string) => (
                  <p key={line}>{line}</p>
                ))}
            </div>
          </div>
          <div className="OutreachContainer__outreach-content-field">
            <div className="OutreachContainer__outreach-content-field-label">
              {t('outreach.form.supportOutreachClearMessageSection.test')}
            </div>
            <div className="OutreachContainer__outreach-content-field-content">
              <div className="OutreachContainer__outreach-content-testNotification">
                <Button
                  disabled={!form.values.supportOutreachTime?.from}
                  styleVariant="quaternary"
                  onClick={() => onClickTestNotification()}
                >
                  <FormattedMessage id="outreach.form.supportOutreachContentSection.testNotification" />
                </Button>

                {!form.values.supportOutreachTime?.from && (
                  <Tooltip
                    openInPortal
                    label={t(
                      'outreach.form.supportOutreachContentSection.testDisabledTooltip',
                    )}
                  >
                    <Icons.InfoIcon color="white" width={14} height={14} />
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </OutreachConfigSection>
  );
};

export default memo(FormikSupportOutreachClearMessageField);
